import React from 'react';
import PropTypes from 'prop-types';
import {Stack, styled, Typography} from '@mui/material';
import {renderAmount} from '../../../../util';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {GREY_90} from '../../../../theme';
import {StyledBodyCell, StyledTableRow} from '../components';
import {noop} from 'lodash';
import {firstColWidth} from '../constants';

const StickyTableCell = styled(StyledBodyCell)(() => ({
  zIndex: 1,
  position: 'sticky',
}));

const HighlightCell = styled(StyledBodyCell)(({highlightColor}) => ({
  userSelect: 'none',
  textAlign: 'right',
  cursor: highlightColor ? 'pointer' : 'inherit',
  '&.MuiTableCell-root': {
    backgroundColor: highlightColor,
  },
}));

export function CashflowRow({
  balanceSheetPliIds,
  cashflowWorkbookData,
  name,
  id,
  amount,
  config,
  depth,
  onExchangeRateClick,
  onAccumulatedDeficitClick,
  onAociClick,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const borderStyles = {};
  if (config.showSingleLineHeader) {
    borderStyles.borderTop = `1px solid ${GREY_90} !important`;
  }
  if (config.showSingleLineFooter) {
    borderStyles.borderBottom = `1px solid ${GREY_90} !important`;
  } else if (config.showDoubleLineFooter) {
    borderStyles.borderBottom = `3px double ${GREY_90} !important`;
  }

  return (
    <StyledTableRow>
      <StickyTableCell
        sx={{
          left: 0,
          width: firstColWidth,
          minWidth: firstColWidth,
          maxWidth: firstColWidth,
        }}
      >
        <Typography
          sx={{
            marginLeft: `${depth * 10}px`,
            fontWeight: config.isBold ? 'bold' : 'normal',
          }}
        >
          {name}
        </Typography>
      </StickyTableCell>

      <StickyTableCell
        sx={{
          left: firstColWidth,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          ...borderStyles,
        }}
      >
        <Stack direction="row">
          <Typography width="100%">
            {!config.isHeaderLine ? (
              <>
                <Typography component="span">
                  {config.showCurrency ? '$ ' : ''}
                </Typography>
                <Typography component="span" sx={{flex: 1, marginLeft: 6}}>
                  {renderAmount(amount, config.invertDisplayAmount)}
                </Typography>
              </>
            ) : null}
          </Typography>
        </Stack>
      </StickyTableCell>
      {balanceSheetPliIds.map((bsPliId, idx) => {
        const data = cashflowWorkbookData.find((d) => {
          return id === d.indirect_pli_id && bsPliId === d.pli_id;
        });

        if (data) {
          let highlightColor = '';
          let onClick = noop;

          const baseUrl = `/statements/indirect-cashflow-workbook/${data.rule_name}`;
          const navigateState = {state: {pliId: bsPliId, indirectPliId: id}};

          if (!data.is_cta && !data.is_formula) {
            highlightColor = '#E1F4D9';
            onClick = () =>
              navigate(
                `${baseUrl}/transactions?${searchParams.toString()}`,
                navigateState
              );
          } else if (!data.is_cta && data.is_formula) {
            highlightColor = '#E1F4D9';
            if (/accounts payable/i.test(data.rule_name)) {
              onClick = () =>
                navigate(
                  `${baseUrl}/unpaid-fixed-assets?${searchParams.toString()}`,
                  navigateState
                );
            } else {
              onClick = () => onAccumulatedDeficitClick(data.amount);
            }
          } else if (data.is_cta && data.is_formula && !data.is_inverted) {
            highlightColor = '#D6F5FF';
            onClick = () => onAociClick();
          } else if (data.is_cta && data.is_formula) {
            highlightColor = '#D6F5FF';
            onClick = () => onExchangeRateClick(bsPliId);
          }

          if (highlightColor) {
            return (
              <HighlightCell
                key={data.rule_name}
                highlightColor={highlightColor}
                onClick={onClick}
              >
                <Typography>{renderAmount(data.amount)}</Typography>
              </HighlightCell>
            );
          }
        }

        return <StyledBodyCell key={`${bsPliId}-${idx}`} />;
      })}
    </StyledTableRow>
  );
}

CashflowRow.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  depth: PropTypes.number.isRequired,
  config: PropTypes.shape({
    hideLine: PropTypes.bool.isRequired,
    invertCalculationAmount: PropTypes.bool.isRequired,
    invertDisplayAmount: PropTypes.bool.isRequired,
    isBold: PropTypes.bool.isRequired,
    isHeaderLine: PropTypes.bool.isRequired,
    isSubcategory: PropTypes.bool.isRequired,
    showCurrency: PropTypes.bool.isRequired,
    showDoubleLineFooter: PropTypes.bool.isRequired,
    showSingleLineFooter: PropTypes.bool.isRequired,
    showSingleLineHeader: PropTypes.bool.isRequired,
  }).isRequired,
  balanceSheetPliIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  onExchangeRateClick: PropTypes.func,
  onAccumulatedDeficitClick: PropTypes.func,
  onAociClick: PropTypes.func,
  cashflowWorkbookData: PropTypes.arrayOf(
    PropTypes.shape({
      bs_fsli: PropTypes.string.isRequired,
      indirect_fsli: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    })
  ).isRequired,
};
